/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { PRODUCT_DOMAIN, SINGLE_TRIP } from "@/constants";
import usePtmmExitModalDetails, {
  modalEventsforBE,
  variationsForBE,
} from "@/hooks/api/quote/ptm-exit-modal-details";
import useStickyLayout from "@/hooks/use-sticky-layout/use-sticky-layout";
import { RootState } from "@/store/root-reducer";

export type TriggerReason = string;

type UseExitIntentTriggerProps = {
  onTrigger: (reason: string) => void;
};

export const sessionStatus = {
  DONE: "DONE",
};

export const STORAGE_KEY = `ptm_exit_modal_status`;
const ENVStatus = ["AVN", "STS"].includes(PRODUCT_DOMAIN);

export function useExitIntentTrigger({ onTrigger }: UseExitIntentTriggerProps) {
  const triggeredRef = useRef(false);
  const { attributes, version } = useSelector(
    (state: RootState) => state.quote
  );
  const isPTMeligible = attributes?.priceResult?.ecoSystem?.eligibility;
  const variation = attributes?.priceResult?.ecoSystem?.variation;
  const tripType = attributes?.tripDetails?.policyType;
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobileDevice } = useStickyLayout();
  const { passModalDetails } = usePtmmExitModalDetails();

  /**
   * Check if the exit intent can be triggered
   */
  const canTriggerExitIntent = useCallback(
    (reason: TriggerReason): boolean => {
      // Already triggered in this session
      if (triggeredRef.current) return false;
      if (isPTMeligible !== true) return false;
      if (
        [variationsForBE.variantA, variationsForBE.variantB].includes(
          `${variation}`
        ) === false
      )
        return false;
      if (tripType !== SINGLE_TRIP) return false;
      // Environment flags are disabled
      if (!ENVStatus) return false;
      if (isMobileDevice && reason === modalEventsforBE.MOUSE_AWAY)
        return false;
      // Already shown in this session (via localStorage)
      if (sessionStorage.getItem(STORAGE_KEY) === sessionStatus.DONE) {
        triggeredRef.current = true;
        return false;
      }
      return true;
    },
    [tripType, triggeredRef, isPTMeligible]
  );

  /**
   * Trigger the exit intent overlay
   */
  const triggerOverlay = useCallback(
    (reason: TriggerReason) => {
      if (!canTriggerExitIntent(reason)) return;
      const modalType = String(`${variation}`);
      triggeredRef.current = true;
      sessionStorage.setItem(STORAGE_KEY, sessionStatus.DONE);
      onTrigger(modalType);
      passModalDetails({
        modalLoaded: true,
        modalLoadedEvent: reason,
        variation: modalType,
        version: `${version}`,
      });
    },
    [canTriggerExitIntent, onTrigger, passModalDetails, version]
  );

  /**
   * Desktop exit intent detection
   * Only active for non-mobile users
   * Triggers when mouse moves out of the viewport (toward browser controls)
   */
  useEffect(() => {
    const isExitingViewport = (event: MouseEvent): boolean => {
      return (
        event.relatedTarget === null &&
        event.clientY <= 0 &&
        event.clientX >= 0 &&
        event.clientX <= window.innerWidth
      );
    };

    const handleMouseOut = (event: MouseEvent) => {
      if (isExitingViewport(event)) {
        triggerOverlay(modalEventsforBE.MOUSE_AWAY);
      }
    };

    document.addEventListener("mouseout", handleMouseOut);

    return () => {
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, [isMobileDevice, triggerOverlay]);

  /**
   * Inactivity detection
   * Active for all users (mobile and desktop)
   * Triggers after 90 seconds of no user activity
   */
  useEffect(() => {
    const clearInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };

    const startInactivityTimer = () => {
      clearInactivityTimer();

      inactivityTimerRef.current = setTimeout(() => {
        triggerOverlay(modalEventsforBE.IDLE);
      }, 90000);
    };

    const resetTimer = () => {
      if (triggeredRef.current) return;
      startInactivityTimer();
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "pointerdown",
    ];

    // Register activity listeners
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetTimer, { passive: true });
    });

    // Start initial timer
    startInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetTimer);
      });
      clearInactivityTimer();
    };
  }, [triggerOverlay]);
}
